import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["categoryTitle", "categorySlogan", "perfumesGrid", "pagination", "dynamicFilterLabel", "dynamicFilterDropdown", "priceFilter"];
  static values = { filterType: String, filterValue: String };

  allRawPerfumes = [];
  filteredAndSortedPerfumes = [];
  currentPage = 1;
  perfumesPerPage = 12;
  currentSecondaryFilterType = '';
  currentSecondaryFilterValue = '';
  currentPriceSort = 'none';

  connect() {
    this.updateHeaderContent();
    this.fetchPerfumes();
    this.setupDynamicFilter();
  }

  updateHeaderContent() {
    let titleText = '';
    let sloganText = '';
    const filterValueLower = this.filterValueValue.toLowerCase();

    if (this.filterTypeValue === 'category') {
      titleText = `Parfums ${this.capitalizeFirstLetter(filterValueLower)}`;
      sloganText = `Découvrez notre sélection de parfums pour ${filterValueLower}.`;
    } else if (this.filterTypeValue === 'fragrance_class') {
      titleText = `Parfums ${this.capitalizeFirstLetter(filterValueLower.replace('_', ' '))}`;
      sloganText = `Explorez l'univers des parfums de classe ${filterValueLower.replace('_', ' ')}.`;
    } else {
      titleText = "Parfums";
      sloganText = "Découvrez toutes nos fragrances.";
    }

    this.categoryTitleTarget.textContent = titleText;
    this.categorySloganTarget.textContent = sloganText;
  }

  async fetchPerfumes() {
    const apiUrl = `/api/v1/parfums`;

    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.allRawPerfumes = data;

      this.applyFiltersAndSortAndRender(false);
    } catch (error) {
      console.error("Error fetching perfumes:", error);
      this.perfumesGridTarget.innerHTML = "<p class='error-message'>Impossible de charger les parfums. Veuillez réessayer plus tard.</p>";
      this.categoryTitleTarget.textContent = "Erreur de chargement";
      this.categorySloganTarget.textContent = "Une erreur est survenue lors du chargement des parfums.";
    }
  }

  setupDynamicFilter() {
    if (this.filterTypeValue === 'category') {
      this.dynamicFilterLabelTarget.textContent = "Filtrer par classe :";
      this.dynamicFilterDropdownTarget.innerHTML = `
        <option value="all_classes">Toutes les classes</option>
        <option value="designer">Designer</option>
        <option value="niche">Niche</option>
        <option value="collection_privee">Collection Privée</option>
      `;
      this.currentSecondaryFilterType = 'fragrance_class';
      this.currentSecondaryFilterValue = 'all_classes';
    } else if (this.filterTypeValue === 'fragrance_class') {
      this.dynamicFilterLabelTarget.textContent = "Filtrer par genre :";
      this.dynamicFilterDropdownTarget.innerHTML = `
        <option value="all_genders">Tous les genres</option>
        <option value="homme">Homme</option>
        <option value="femme">Femme</option>
        <option value="unisexe">Unisexe</option>
      `;
      this.currentSecondaryFilterType = 'category';
      this.currentSecondaryFilterValue = 'all_genders';
    }
  }

  filterPerfumes(event) {
    if (event.currentTarget === this.dynamicFilterDropdownTarget) {
      this.currentSecondaryFilterValue = event.target.value;
    } else if (event.currentTarget === this.priceFilterTarget) {
      this.currentPriceSort = event.target.value;
    }
    this.applyFiltersAndSortAndRender();
  }

  applyFiltersAndSortAndRender(resetPage = true) {
    let tempPerfumes = [...this.allRawPerfumes];

    // 1. Apply primary filter from URL
    tempPerfumes = tempPerfumes.filter(perfume => {
      const perfumeCategoryLower = perfume.category ? perfume.category.toLowerCase() : '';
      const perfumeFragranceClassLower = perfume.fragrance_class ? perfume.fragrance_class.toLowerCase() : '';
      const urlFilterValueLower = this.filterValueValue.toLowerCase();

      if (this.filterTypeValue === 'category') {
        return perfumeCategoryLower === urlFilterValueLower;
      } else if (this.filterTypeValue === 'fragrance_class') {
        return perfumeFragranceClassLower === urlFilterValueLower;
      }
      return true;
    });

    // 2. Apply secondary filter
    if (this.currentSecondaryFilterType === 'category' && this.currentSecondaryFilterValue !== 'all_genders') {
      tempPerfumes = tempPerfumes.filter(perfume => {
        const perfumeCategoryLower = perfume.category ? perfume.category.toLowerCase() : '';
        return perfumeCategoryLower === this.currentSecondaryFilterValue;
      });
    } else if (this.currentSecondaryFilterType === 'fragrance_class' && this.currentSecondaryFilterValue !== 'all_classes') {
      tempPerfumes = tempPerfumes.filter(perfume => {
        const perfumeFragranceClassLower = perfume.fragrance_class ? perfume.fragrance_class.toLowerCase() : '';
        return perfumeFragranceClassLower === this.currentSecondaryFilterValue;
      });
    }

    // 3. Apply price sort
    if (this.currentPriceSort === 'asc') {
      tempPerfumes.sort((a, b) => this.getMinPrice(a) - this.getMinPrice(b));
    } else if (this.currentPriceSort === 'desc') {
      tempPerfumes.sort((a, b) => this.getMinPrice(b) - this.getMinPrice(a));
    }

    this.filteredAndSortedPerfumes = tempPerfumes;

    if (resetPage) {
      this.currentPage = 1;
    } else {
      const totalPages = Math.max(1, Math.ceil(this.filteredAndSortedPerfumes.length / this.perfumesPerPage));
      this.currentPage = this.restoreSavedPage(totalPages);
    }

    if (this.filteredAndSortedPerfumes.length === 0) {
      this.perfumesGridTarget.innerHTML = "<p class='no-perfumes-message'>Aucun parfum trouvé pour cette sélection pour le moment.</p>";
      this.paginationTarget.innerHTML = '';
      return;
    }

    this.renderPerfumes();
    this.renderPagination();
  }

  renderPerfumes() {
    const startIndex = (this.currentPage - 1) * this.perfumesPerPage;
    const endIndex = startIndex + this.perfumesPerPage;
    const perfumesToDisplay = this.filteredAndSortedPerfumes.slice(startIndex, endIndex);

    const perfumeHtml = perfumesToDisplay.map(perfume => {
      const imageUrl = perfume.image_url || "/placeholder.svg?height=250&width=250&text=Parfum";
      const availabilityStatus = perfume.disponible ? 'Disponible' : 'Rupture de stock';
      const availabilityClass = perfume.disponible ? 'perfume-availability-badge--available' : 'perfume-availability-badge--unavailable';

      // FIX: Obtenir le prix minimum et la première variante
      const minPrice = this.getMinPrice(perfume);
      const firstVariant = perfume.variants && perfume.variants.length > 0 ? perfume.variants[0] : null;
      const defaultSize = firstVariant ? firstVariant.size : '10ml';
      const defaultPrice = firstVariant ? firstVariant.price : minPrice;

      return `
        <div class="perfume-card">
          <a href="/vitrine/parfums/${perfume.slug || perfume.id}" class="perfume-card-link">
            <div class="perfume-image-wrapper">
              <img src="${imageUrl}" alt="${perfume.name}" class="perfume-image">
              <div class="image-overlay"></div>
            </div>
            <div class="perfume-content">
              <h3 class="perfume-name">${perfume.name}</h3>
              <p class="perfume-brand">${perfume.brand ? perfume.brand.name : 'Inconnue'}</p>
              <p class="perfume-description">${this.truncateText(perfume.description, 100)}</p>
              <div class="perfume-meta">
                <span class="perfume-category">${perfume.category ? this.capitalizeFirstLetter(perfume.category) : ''}</span>
                <span class="perfume-class">${perfume.fragrance_class ? this.capitalizeFirstLetter(perfume.fragrance_class) : ''}</span>
              </div>
              <p class="perfume-price">${this.formatCurrency(minPrice)}</p>
              <div class="perfume-volume">${defaultSize}</div>
              <div class="perfume-card-actions">
                <button
                  class="perfume-card-add-to-cart-btn"
                  data-action="click->cart#addToCart"
                  data-parfum-id="${perfume.id}"
                  data-parfum-name="${perfume.name}"
                  data-parfum-price="${defaultPrice}"
                  data-parfum-size="${defaultSize}"
                  data-parfum-image-url="${imageUrl}"
                  ${!perfume.disponible ? 'disabled' : ''}
                >
                  Ajouter au panier
                </button>
              </div>
            </div>
          </a>
          <div class="perfume-availability-badge ${availabilityClass}">${availabilityStatus}</div>
        </div>
      `;
    }).join('');

    this.perfumesGridTarget.innerHTML = perfumeHtml;
  }

  renderPagination() {
    const totalPages = Math.ceil(this.filteredAndSortedPerfumes.length / this.perfumesPerPage);
    if (totalPages <= 1) {
      this.paginationTarget.innerHTML = '';
      return;
    }

    let paginationHtml = '<ul class="pagination">';
    paginationHtml += `
      <li class="page-item ${this.currentPage === 1 ? 'disabled' : ''}">
        <a href="#" data-action="click->category-perfumes#previousPage" class="page-link">Précédent</a>
      </li>
    `;
    for (const page of this.getPageNumbers(this.currentPage, totalPages)) {
      if (page === '...') {
        paginationHtml += `
          <li class="page-item disabled page-item--ellipsis">
            <span class="page-link">&hellip;</span>
          </li>
        `;
      } else {
        paginationHtml += `
          <li class="page-item ${this.currentPage === page ? 'current' : ''}">
            <a href="#" data-action="click->category-perfumes#goToPage" data-page="${page}" class="page-link">${page}</a>
        </li>
        `;
      }
    }
    paginationHtml += `
      <li class="page-item ${this.currentPage === totalPages ? 'disabled' : ''}">
        <a href="#" data-action="click->category-perfumes#nextPage" class="page-link">Suivant</a>
      </li>
    `;
    paginationHtml += '</ul>';
    this.paginationTarget.innerHTML = paginationHtml;
  }

  previousPage(event) {
    event.preventDefault();
    if (this.currentPage > 1) {
      this.currentPage--;
      this.savePage();
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  nextPage(event) {
    event.preventDefault();
    const totalPages = Math.ceil(this.filteredAndSortedPerfumes.length / this.perfumesPerPage);
    if (this.currentPage < totalPages) {
      this.currentPage++;
      this.savePage();
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  goToPage(event) {
    event.preventDefault();
    const page = parseInt(event.currentTarget.dataset.page);
    if (page && page !== this.currentPage) {
      this.currentPage = page;
      this.savePage();
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Construit la liste des numéros de page à afficher, avec des "..." pour les écarts
  getPageNumbers(current, total) {
    const pages = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  }

  getPageStorageKey() {
    return `pagination:${window.location.pathname}${window.location.search}`;
  }

  restoreSavedPage(totalPages) {
    try {
      const saved = parseInt(sessionStorage.getItem(this.getPageStorageKey()), 10);
      if (saved >= 1 && saved <= totalPages) {
        return saved;
      }
    } catch (e) {}
    return 1;
  }

  savePage() {
    try {
      sessionStorage.setItem(this.getPageStorageKey(), String(this.currentPage));
    } catch (e) {}
  }

  // NOUVELLE MÉTHODE: Obtenir le prix minimum
  getMinPrice(perfume) {
    if (!perfume.variants || perfume.variants.length === 0) {
      return 0;
    }
    return Math.min(...perfume.variants.map(v => parseFloat(v.price)));
  }

  formatCurrency(amount) {
    const num = parseFloat(amount);
    if (isNaN(num)) return "0,00 TND";
    return new Intl.NumberFormat('fr-TN', { 
      style: 'currency', 
      currency: 'TND',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  }

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  }

  capitalizeFirstLetter(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
}